"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.pathGeographyUtils = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const get_1 = __importDefault(require("lodash/get"));
const turf = __importStar(require("@turf/turf"));
const Preferences_1 = __importDefault(require("chaire-lib-common/lib/config/Preferences"));
const RoutingServiceManager_1 = __importDefault(require("chaire-lib-common/lib/services/routing/RoutingServiceManager"));
const PathGeographyGenerator_1 = require("./PathGeographyGenerator");
const PhysicsUtils_1 = require("chaire-lib-common/lib/utils/PhysicsUtils");
const MathUtils_1 = require("chaire-lib-common/lib/utils/MathUtils");
/*
DEFAULT_MIN_MATCHING_TIMESTAMP is the minimum interval between timestamps for the matching routing (using OSRM).
This seems to be the minimum value for which we can match correctly with long segments between nodes.
TODO: analyze OSRM matching algorithm to find a better value or make sure it never fails for our usage
DEFAULT_ROUTING_SPEED_MPS is another arbitrary speed for matching which makes it not fail. Needs investigation.
*/
const DEFAULT_MIN_MATCHING_TIMESTAMP = 500; // can be overriden by data.minMatchingTimestamp, in seconds
const DEFAULT_ROUTING_SPEED_MPS = 20;
const DEFAULT_WAYPOINT_RADIUS_METERS = 15;
class PathGeographyUtils {
    constructor() {
        this.calculateBirdDistanceDuration = (from, to, defaultRunningSpeedMps) => {
            const lineWithPrevious = turf.lineString([from.geometry.coordinates, to.geometry.coordinates]);
            const lineWithPreviousLength = turf.length(lineWithPrevious, { units: 'meters' });
            // Using a default speed of 15 km/h seems just fine with map matching (faster speeds makes osrm fails to find some routes):
            return (0, MathUtils_1.ceilToPositiveInteger)(lineWithPreviousLength / Math.max(defaultRunningSpeedMps, (0, PhysicsUtils_1.kphToMps)(15)));
        };
        this.initializePointGeojsonCollection = () => {
            return { type: 'FeatureCollection', features: [] };
        };
        this.prepareNodesAndWaypoints = (path, defaultRunningSpeedMps, routingEngine = 'engine') => {
            const nodeIds = path.get('nodes', []);
            const nodeTypes = path.getData('nodeTypes', []);
            // Modes such as gondola keep a straight cable between stations.
            const ignoreWaypoints = path.allowsWaypoints() === false;
            const waypoints = ignoreWaypoints ? [] : path.getData('waypoints', []);
            const waypointTypes = ignoreWaypoints ? [] : path.getData('waypointTypes', []);
            // prepare matching query:
            const nodesAndWaypointsGeojsons = this.initializePointGeojsonCollection();
            const defaultRoutingRadiusMeters = Preferences_1.default.current.transit.nodes.defaultRoutingRadiusMeters;
            // if activated: force temporary increase the nodes routing radius for this path calculation if the existing shape is not passing through it at some nodes:
            // TODO: test this
            const distancesForNodeIdsWithRoutingRadiusTooSmallForPathShape = routingEngine === 'engine' &&
                (path.getData('from_gtfs', false) || path.getData('increaseRoutingRadiiToIncludeExistingPathShape', false))
                ? path.getDistancesForNodeIdsWithRoutingRadiusTooSmallForPathShape()
                : [];
            let currentTime = 0;
            // increment each node coordinates and radii (radiuses):
            nodeIds.forEach((nodeId, nodeIndex) => {
                // Copy the node
                const nodeGeojson = (0, cloneDeep_1.default)(path._collectionManager.get('nodes').getById(nodeId));
                const nodeProperties = nodeGeojson.properties || {};
                nodeGeojson.properties = nodeProperties;
                nodeProperties.isNode = true;
                // Some other type like manual may have been specified
                nodeProperties.type = nodeTypes[nodeIndex] || routingEngine;
                const nodeRoutingRadius = (0, get_1.default)(nodeGeojson, 'properties.routing_radius_meters', defaultRoutingRadiusMeters);
                nodeProperties.radius = Math.min(200, distancesForNodeIdsWithRoutingRadiusTooSmallForPathShape[nodeId]
                    ? distancesForNodeIdsWithRoutingRadiusTooSmallForPathShape[nodeId] + 5 // add a 5m buffer to make sure we have enough margin for map matching
                    : nodeRoutingRadius);
                // Calculate approximate timestamp to reach this point
                // timestamps are a bit arbitrary since we do not match a real GPS trace.
                if (nodeIndex !== 0) {
                    currentTime += Math.max(path.getData('minMatchingTimestamp', DEFAULT_MIN_MATCHING_TIMESTAMP), this.calculateBirdDistanceDuration(nodesAndWaypointsGeojsons.features[nodesAndWaypointsGeojsons.features.length - 1], nodeGeojson, DEFAULT_ROUTING_SPEED_MPS));
                }
                nodeProperties.timestamp = currentTime;
                nodesAndWaypointsGeojsons.features.push(nodeGeojson);
                // get waypoints between this node and next:
                if (waypoints[nodeIndex]) {
                    waypoints[nodeIndex].forEach((waypoint, waypointIndex) => {
                        const waypointFeature = turf.point(waypoint, {
                            isNode: false,
                            type: waypointTypes[nodeIndex][waypointIndex],
                            radius: DEFAULT_WAYPOINT_RADIUS_METERS
                        });
                        currentTime += Math.max(path.getData('minMatchingTimestamp', DEFAULT_MIN_MATCHING_TIMESTAMP), this.calculateBirdDistanceDuration(nodesAndWaypointsGeojsons.features[nodesAndWaypointsGeojsons.features.length - 1], waypointFeature, DEFAULT_ROUTING_SPEED_MPS));
                        waypointFeature.properties = waypointFeature.properties || {};
                        waypointFeature.properties.timestamp = currentTime;
                        nodesAndWaypointsGeojsons.features.push(waypointFeature);
                    });
                }
            });
            return nodesAndWaypointsGeojsons;
        };
        this.shouldPathUpdate = (path) => {
            const nodeIds = path.get('nodes', []);
            const waypoints = path.getData('waypoints', []);
            const waypointTypes = path.getData('waypointTypes', []);
            for (let i = 0, count = nodeIds.length; i < count; i++) {
                if (!waypoints[i]) {
                    waypoints[i] = [];
                }
                if (!waypointTypes[i]) {
                    waypointTypes[i] = [];
                }
            }
            path.attributes.data.waypoints = waypoints;
            path.attributes.data.waypointTypes = waypointTypes;
            // Modes that disallow waypoints must not be routed from a leftover shape point.
            const hasWaypoint = path.allowsWaypoints() !== false && waypoints[0] && waypoints[0].length > 0;
            // There is less than 1 node and no waypoints, empty the path
            if (nodeIds.length < 2 && !hasWaypoint) {
                return false;
            }
            const line = path.getLine();
            // Can't update geography if there's no line or no node
            return line && path._collectionManager.get('nodes');
        };
        this.getRoutingSegments = (nodesAndWaypointsGeojsons, routingEngine) => {
            const routingSegments = [];
            let routingSegmentGeojson = this.initializePointGeojsonCollection();
            let lastRoutingType = routingEngine;
            nodesAndWaypointsGeojsons.features.forEach((geojson, _geojsonIndex) => {
                var _a;
                const nodeRoutingType = routingEngine === 'manual' ? 'manual' : ((_a = geojson.properties) === null || _a === void 0 ? void 0 : _a.type) || lastRoutingType;
                if (nodeRoutingType !== lastRoutingType && routingSegmentGeojson.features.length > 0) {
                    // Save current segment and create a new one, adding the last point from previous segment
                    const lastGeojson = routingSegmentGeojson.features[routingSegmentGeojson.features.length - 1];
                    routingSegments.push({ routingType: lastRoutingType, geojson: routingSegmentGeojson });
                    routingSegmentGeojson = this.initializePointGeojsonCollection();
                    routingSegmentGeojson.features.push(lastGeojson);
                }
                routingSegmentGeojson.features.push(geojson);
                lastRoutingType = nodeRoutingType;
            });
            if (routingSegmentGeojson.features.length > 0) {
                routingSegments.push({ routingType: lastRoutingType, geojson: routingSegmentGeojson });
            }
            return routingSegments;
        };
        this.routeSegments = (routingSegments, routingMode, defaultRunningSpeedMps) => __awaiter(this, void 0, void 0, function* () {
            const matchings = [];
            yield Promise.allSettled(routingSegments.map((routingSegment, routingSegmentIndex) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const routingService = RoutingServiceManager_1.default.getRoutingServiceForEngine(routingSegment.routingType);
                    const routingResult = yield routingService.mapMatch({
                        mode: routingMode,
                        points: routingSegment.geojson,
                        defaultRunningSpeed: defaultRunningSpeedMps
                    });
                    matchings[routingSegmentIndex] = routingResult;
                }
                catch (error) {
                    console.error('error routing segment %d: %s', routingSegmentIndex, error);
                    matchings[routingSegmentIndex] = {
                        tracepoints: routingSegment.geojson.features.map(() => null),
                        matchings: []
                    };
                }
                return matchings[routingSegmentIndex];
            })));
            return matchings;
        });
        this.getTerminalGeojson = (nodesAndWaypointsGeojsons) => {
            return {
                type: 'FeatureCollection',
                features: [
                    nodesAndWaypointsGeojsons.features[0],
                    nodesAndWaypointsGeojsons.features[nodesAndWaypointsGeojsons.features.length - 1]
                ]
            };
        };
        this.getPathGeography = (path) => __awaiter(this, void 0, void 0, function* () {
            if (!this.shouldPathUpdate(path)) {
                return false;
            }
            const routingEngine = path.getData('routingEngine');
            const routingMode = path.getData('routingMode');
            const routingService = RoutingServiceManager_1.default.getRoutingServiceForEngine(routingEngine);
            const defaultRunningSpeedMps = (0, PhysicsUtils_1.kphToMps)(path.getData('defaultRunningSpeedKmH', 15));
            /*if (!routingMode || !routingEngine)
            {
                path.attributes.data.geographyErrors = { error: 'transit:transitPath:errors:routingModeNecessary'};
            }*/
            const nodesAndWaypointsGeojsons = this.prepareNodesAndWaypoints(path, defaultRunningSpeedMps, routingEngine);
            try {
                // The direct path serves as the base to compare with the route
                const directPath = yield this.getDirectPath(routingService, routingMode, defaultRunningSpeedMps, nodesAndWaypointsGeojsons);
                const routingSegments = this.getRoutingSegments(nodesAndWaypointsGeojsons, routingEngine);
                const routingResults = yield this.routeSegments(routingSegments, routingMode, defaultRunningSpeedMps);
                return { direct: directPath, points: nodesAndWaypointsGeojsons, segmentResults: routingResults };
            }
            catch (error) {
                if (error.nodes) {
                    path.attributes.data.routingFailed = true;
                    path.attributes.data.geographyErrors = error;
                }
                return false;
            }
        });
    }
    getErrorsForDirectPath(nodesAndWaypointsGeojsons, error = {}) {
        var _a, _b;
        const nodesWithErrors = [];
        const waypointsWithErrors = [];
        const firstPoint = nodesAndWaypointsGeojsons.features[0];
        if ((_a = firstPoint.properties) === null || _a === void 0 ? void 0 : _a.isNode) {
            nodesWithErrors.push(firstPoint);
        }
        else {
            waypointsWithErrors.push(firstPoint);
        }
        const lastPoint = nodesAndWaypointsGeojsons.features[nodesAndWaypointsGeojsons.features.length - 1];
        if ((_b = lastPoint.properties) === null || _b === void 0 ? void 0 : _b.isNode) {
            nodesWithErrors.push(lastPoint);
        }
        else {
            waypointsWithErrors.push(lastPoint);
        }
        const pointErrors = {
            nodes: nodesWithErrors,
            waypoints: waypointsWithErrors
        };
        if (error.error) {
            pointErrors.error = error.error;
        }
        return pointErrors;
    }
    getDirectPath(routingService, routingMode, defaultRunningSpeedMps, nodesAndWaypointsGeojsons) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const terminals = this.getTerminalGeojson(nodesAndWaypointsGeojsons);
                const shouldUseManual = terminals.features.find((feature) => { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.type) === 'manual'; }) !== undefined;
                const directPathRoutingService = shouldUseManual
                    ? RoutingServiceManager_1.default.getRoutingServiceForEngine('manual')
                    : routingService;
                const directPath = yield directPathRoutingService.mapMatch({
                    mode: routingMode,
                    defaultRunningSpeed: defaultRunningSpeedMps,
                    points: terminals
                });
                return directPath;
            }
            catch (error) {
                // TODO Why is the error type an object? Investigate if we can have something more generic (or not)
                throw this.getErrorsForDirectPath(nodesAndWaypointsGeojsons, error);
            }
        });
    }
}
exports.pathGeographyUtils = new PathGeographyUtils();
/**
 * Drop stored waypoints once geography generation has finished for a mode that
 * does not accept them. Gondola cables are a straight line between stations, so
 * leftover shape points would no longer match the geometry.
 * Editing methods leave those points in place until this runs.
 * @param path Path whose geography was just generated
 */
const dropWaypointsWhenDisallowed = (path) => {
    var _a, _b;
    if (path.allowsWaypoints()) {
        return;
    }
    const nodeCount = (_b = (_a = path.attributes.nodes) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    path.attributes.data.waypoints = Array.from({ length: nodeCount }, () => []);
    path.attributes.data.waypointTypes = Array.from({ length: nodeCount }, () => []);
};
const updateGeography = (path, changesInfo) => __awaiter(void 0, void 0, void 0, function* () {
    // TODO: Make the geography errors part of the path, when refactoring the class
    delete path.attributes.data.geographyErrors;
    path.attributes.data.routingFailed = false;
    const geographyResults = yield exports.pathGeographyUtils.getPathGeography(path);
    if (!geographyResults) {
        // TODO Move emptyGeography to PathGeographyGenerator.ts
        path.emptyGeography();
        path.validate();
        path.refreshStats();
        return { path };
    }
    const { direct, points, segmentResults } = geographyResults;
    // Process the direct path result and update the base distances/times
    if (direct.matchings.length === 0 && direct.tracepoints.some((tp) => tp === null)) {
        // Error getting the direct point, one of the terminal points is not defined
        path.attributes.data.routingFailed = true;
        path.attributes.data.geographyErrors = exports.pathGeographyUtils.getErrorsForDirectPath(points);
        return { path };
    }
    else {
        path.attributes.data.directRouteBetweenTerminalsTravelTimeSeconds = (0, MathUtils_1.ceilToPositiveInteger)(direct.matchings[0].legs[0].duration);
        path.attributes.data.directRouteBetweenTerminalsDistanceMeters = (0, MathUtils_1.ceilToPositiveInteger)(direct.matchings[0].legs[0].distance);
    }
    try {
        (0, PathGeographyGenerator_1.generatePathGeographyFromRouting)(path, points, segmentResults, changesInfo);
        dropWaypointsWhenDisallowed(path);
        path.validate();
        path.refreshStats();
        return { path };
    }
    catch (error) {
        console.log(error);
        path.emptyGeography();
        path.validate();
        path.refreshStats();
        return { path };
    }
});
exports.default = updateGeography;
//# sourceMappingURL=PathGeographyUtils.js.map